import type { CSSProperties } from 'react';
import { theme, styles } from '@/lib/uiTheme';

export const listingFormStyles = {
  page: styles.productPage({
    background: `linear-gradient(180deg, ${theme.colors.surfaceMuted} 0%, ${theme.colors.surface} 48%, ${theme.colors.surfaceSubtle} 100%)`,
    minHeight: '100vh',
    paddingBottom: 96,
    width: '100%',
    maxWidth: '100%',
    overflowX: 'clip',
  }),
  header: styles.productPageHeader(),
  title: styles.productPageTitle(),
  subtitle: styles.productPageSubtitle(),
  panel: styles.panelPremium({
    padding: theme.spacing['2xl'],
    marginBottom: theme.spacing.lg,
  }),
  sectionTitle: {
    margin: `0 0 ${theme.spacing.md}px`,
    fontSize: theme.typography.lg,
    fontWeight: theme.typography.bold,
    color: theme.colors.ink,
  } as CSSProperties,
  sectionHint: {
    margin: `0 0 ${theme.spacing.lg}px`,
    fontSize: theme.typography.sm,
    color: theme.colors.inkSubtle,
    lineHeight: 1.45,
  } as CSSProperties,
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  } as CSSProperties,
  label: {
    ...styles.label(),
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  } as CSSProperties,
  required: {
    color: theme.colors.danger,
    fontWeight: theme.typography.semibold,
    fontSize: theme.typography.xs,
  } as CSSProperties,
  optional: {
    color: theme.colors.inkSubtle,
    fontWeight: theme.typography.normal,
    fontSize: theme.typography.xs,
  } as CSSProperties,
  input: styles.input({
    background: theme.colors.surface,
    color: theme.colors.ink,
  }),
  inputError: styles.input({
    background: theme.colors.surface,
    color: theme.colors.ink,
    borderColor: theme.colors.danger,
  }),
  textarea: {
    ...styles.input({
      background: theme.colors.surface,
      color: theme.colors.ink,
      height: 'auto',
      minHeight: 120,
      padding: theme.spacing.md,
      resize: 'vertical',
    }),
  } as CSSProperties,
  select: styles.input({
    background: theme.colors.surface,
    color: theme.colors.ink,
  }),
  fieldError: {
    marginTop: 6,
    fontSize: theme.typography.xs,
    color: theme.colors.danger,
  } as CSSProperties,
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
    gap: theme.spacing.md,
  } as CSSProperties,
  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
    gap: theme.spacing.md,
  } as CSSProperties,
  navBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing.xl,
    paddingTop: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.border}`,
  } as CSSProperties,
  stickyNav: {
    position: 'sticky',
    bottom: 0,
    zIndex: 20,
    marginTop: theme.spacing.xl,
    padding: `${theme.spacing.md}px clamp(8px, 3vw, ${theme.spacing.lg}px)`,
    background: 'rgba(255,255,255,0.94)',
    borderTop: `1px solid ${theme.colors.border}`,
    backdropFilter: 'blur(8px)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    justifyContent: 'space-between',
    alignItems: 'center',
  } as CSSProperties,
  progressWrap: {
    display: 'flex',
    gap: theme.spacing.sm,
    flexWrap: 'wrap',
    marginBottom: theme.spacing.xl,
  } as CSSProperties,
  progressStep: (active: boolean, done: boolean): CSSProperties => ({
    flex: '1 1 120px',
    minWidth: 100,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    borderRadius: theme.radii.lg,
    border: `1px solid ${active ? theme.colors.primary : theme.colors.border}`,
    background: active
      ? theme.colors.primaryMuted
      : done
        ? theme.colors.successMuted
        : theme.colors.surface,
    color: active ? theme.colors.primaryHover : theme.colors.inkMuted,
    fontSize: theme.typography.sm,
    fontWeight: theme.typography.semibold,
  }),
  errorBanner: {
    background: theme.colors.dangerMuted,
    color: theme.colors.danger,
    border: `1px solid #fecaca`,
    borderRadius: theme.radii.lg,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  } as CSSProperties,
  validationSummary: {
    background: theme.colors.dangerMuted,
    color: theme.colors.danger,
    border: '1px solid #fecaca',
    borderRadius: theme.radii.lg,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg,
    overflowWrap: 'anywhere',
  } as CSSProperties,
  validationLink: {
    appearance: 'none',
    border: 0,
    padding: '3px 0',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    textDecoration: 'underline',
    cursor: 'pointer',
  } as CSSProperties,
  successBanner: {
    background: theme.colors.successMuted,
    color: theme.colors.successHover,
    border: `1px solid #a7f3d0`,
    borderRadius: theme.radii.lg,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
  } as CSSProperties,
  btnPrimary: styles.buttonPrimary({ minHeight: 44, maxWidth: '100%' }),
  btnGhost: styles.buttonGhost({ minHeight: 44, maxWidth: '100%' }),
  btnDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  } as CSSProperties,
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    cursor: 'pointer',
    userSelect: 'none',
  } as CSSProperties,
  pinStatus: {
    marginTop: theme.spacing.sm,
    fontSize: theme.typography.sm,
    color: theme.colors.inkMuted,
  } as CSSProperties,
  mapFrame: {
    marginTop: theme.spacing.md,
    height: 320,
    borderRadius: theme.radii.card,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.border}`,
    background: theme.colors.surfaceSubtle,
  } as CSSProperties,
  reviewGroup: {
    marginBottom: theme.spacing.lg,
  } as CSSProperties,
  reviewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    padding: `${theme.spacing.sm}px 0`,
    borderBottom: `1px solid ${theme.colors.border}`,
    fontSize: theme.typography.sm,
  } as CSSProperties,
  reviewLabel: {
    color: theme.colors.inkSubtle,
  } as CSSProperties,
  reviewValue: {
    color: theme.colors.ink,
    fontWeight: theme.typography.medium,
    textAlign: 'right',
  } as CSSProperties,
};
